import { useAppDispatch, useAppSelector } from 'redux/hooks'
import Location from 'interfaces/Location'
import {
  resetFormulaire,
  toggleDisplayMode as toggleDisplayModeAction,
  updateEndDate,
  updateHabitableMax,
  updateHabitableMin,
  updateLocations,
  updateMontantMax,
  updateMontantMin,
  updateNombrePieces,
  updateStartDate,
  updateTerrainMax,
  updateTerrainMin,
  updateTypeBien,
} from 'redux/features/etudierMarche/slice'
import {
  makeSelectDisplayMode,
  makeSelectEndDate,
  makeSelectLocations,
  makeSelectMontantMax,
  makeSelectMontantMin,
  makeSelectNombrePieces,
  makeSelectStartDate,
  makeSelectSurfaceHabitableMax,
  makeSelectSurfaceHabitableMin,
  makeSelectSurfaceTerrainMax,
  makeSelectSurfaceTerrainMin,
  makeSelectTypeBien,
} from 'redux/features/etudierMarche/selectors'
import {
  failedExportFile,
  idleDvf,
  idleExportFile,
  loadingExportFile,
  selectParcelle,
  Status,
  succeededExportFile,
} from 'redux/features/dvf/slice'
import {
  makeSelectExportFileBlob,
  makeSelectStatusExportFile,
} from 'redux/features/dvf/selectors'
import { APISearchDVF } from 'interfaces/API'
import { Mutation, Parcelle } from 'interfaces/Dvf'
import { useEffect } from 'react'
import { fetchWithCheckAuthorization } from 'utils/fetchWithAuthorization'
import {
  makeSelectPrincipalBounds,
  makeSelectSearchCircle,
} from 'redux/features/map/selectors'
import useDvf from './useDvf'
import useDvfNavigation from './useDvfNavigation'
import useGoogleMap from './useGoogleMap'

interface PlaceholderForm {
  surfaceHabitableMax: number
  montantTransactionMontantMax: number
  surfaceTerrainMax: number
}

interface ReturnProps {
  locations: Location[]
  typeBien: number[] | null
  nombrePieces: number[]
  surfaceHabitableMin: number
  surfaceHabitableMax: number | null
  surfaceTerrainMin: number
  surfaceTerrainMax: number | null
  montantMin: number
  montantMax: number | null
  startDate: Date | null
  endDate: Date | null
  setLocations: (newLocations: Location[]) => void
  setTypeDeBien: (newValue: number) => void
  setNombrePieces: (newPiece: number) => void
  setSurfaceHabitableMin: (newSurfaceHabitableMin: number) => void
  setSurfaceHabitableMax: (newSurfaceHabitableMax: number | null) => void
  setSurfaceTerrainMin: (newSurfaceTerrainMin: number) => void
  setSurfaceTerrainMax: (newSurfaceTerrainMax: number | null) => void
  setMontantMin: (newMontantMin: number) => void
  setMontantMax: (newMontantMax: number | null) => void
  setStartDate: (newStartDate: Date | null) => void
  setEndDate: (newEndDate: Date | null) => void
  onSubmit: () => void
  onEffacerCriteresClick: () => void
  onNouvelleEtudeClick: () => void
  onEditClick: () => void
  onCardDvfClick: (uid: string) => void
  onMarkerClick: (uid: string) => void
  exportToCsv: () => void
  parcelles: Parcelle[]
  mutations: { [uid: string]: Mutation }
  statusDvf: Status
  errorDvf: string | null
  displayMode: 'normal' | 'synthese'
  nextParcelle: () => void
  previousParcelle: () => void
  isNextAvailable: boolean
  isPreviousAvailable: boolean
  placeholderForm: PlaceholderForm
}

const useEtudierMarche = (): ReturnProps => {
  const locations = useAppSelector(makeSelectLocations)
  const typeBien = useAppSelector(makeSelectTypeBien)
  const nombrePieces = useAppSelector(makeSelectNombrePieces)
  const surfaceHabitableMin = useAppSelector(makeSelectSurfaceHabitableMin)
  const surfaceHabitableMax = useAppSelector(makeSelectSurfaceHabitableMax)
  const surfaceTerrainMin = useAppSelector(makeSelectSurfaceTerrainMin)
  const surfaceTerrainMax = useAppSelector(makeSelectSurfaceTerrainMax)
  const montantMin = useAppSelector(makeSelectMontantMin)
  const montantMax = useAppSelector(makeSelectMontantMax)
  const startDate = useAppSelector(makeSelectStartDate)
  const endDate = useAppSelector(makeSelectEndDate)
  const displayMode = useAppSelector(makeSelectDisplayMode)

  const exportFileBlob = useAppSelector(makeSelectExportFileBlob)
  const statusExportFile = useAppSelector(makeSelectStatusExportFile)

  const principalBounds = useAppSelector(makeSelectPrincipalBounds)
  const searchCircle = useAppSelector(makeSelectSearchCircle)

  const { parcelles, mutations, statusDvf, errorDvf, fetchSearchDVF } = useDvf()
  const dispatch = useAppDispatch()
  const map = useGoogleMap()
  const {
    isNextAvailable,
    isPreviousAvailable,
    nextParcelle,
    previousParcelle,
  } = useDvfNavigation()

  const setLocations = (newLocations: Location[]) =>
    dispatch(updateLocations(newLocations))

  const setTypeDeBien = (newValue: number) => dispatch(updateTypeBien(newValue))

  const setNombrePieces = (newPiece: number) =>
    dispatch(updateNombrePieces(newPiece))

  const setSurfaceHabitableMin = (newSurfaceHabitableMin: number) =>
    dispatch(updateHabitableMin(newSurfaceHabitableMin))

  const setSurfaceHabitableMax = (newSurfaceHabitableMax: number | null) =>
    dispatch(updateHabitableMax(newSurfaceHabitableMax))

  const setSurfaceTerrainMin = (newSurfaceTerrainMin: number) =>
    dispatch(updateTerrainMin(newSurfaceTerrainMin))

  const setSurfaceTerrainMax = (newSurfaceTerrainMax: number | null) =>
    dispatch(updateTerrainMax(newSurfaceTerrainMax))

  const setMontantMin = (newMontantMin: number) =>
    dispatch(updateMontantMin(newMontantMin))

  const setMontantMax = (newMontantMax: number | null) =>
    dispatch(updateMontantMax(newMontantMax))

  const setStartDate = (newStartDate: Date | null) => {
    dispatch(updateStartDate(newStartDate))
  }

  const setEndDate = (newEndDate: Date | null) =>
    dispatch(updateEndDate(newEndDate))

  const placeholderForm = {
    surfaceHabitableMax: 1000,
    montantTransactionMontantMax: 1000000,
    surfaceTerrainMax: 6000,
  }

  const getDataForm = () => {
    const searchData: APISearchDVF = {
      cityCode: null,
      typeBien,
      nombrePieces,
      montantTransaction: {
        min: montantMin,
        max: montantMax,
      },
      dateTransaction: {
        min: startDate || new Date('2014'),
        max: endDate || new Date(),
      },
      searchZone: null,
      aireParcelle: {
        min: surfaceTerrainMin,
        max: surfaceTerrainMax,
      },
      surfaceHabitable: {
        min: surfaceHabitableMin,
        max: surfaceHabitableMax,
      },
    }
    if (searchCircle !== undefined) {
      searchData.searchZone = {
        rayon: searchCircle.radius,
        centre: {
          latitude: searchCircle.center.lat,
          longitude: searchCircle.center.lng,
        },
      }
    } else searchData.cityCode = locations.map(({ codeInsee }) => codeInsee)
    return searchData
  }

  const submitForm = async () => fetchSearchDVF(getDataForm())

  const toggleDisplayMode = (newDisplayMode?: 'normal' | 'synthese') => {
    dispatch(toggleDisplayModeAction(newDisplayMode))
  }

  const onEffacerCriteresClick = () => {
    dispatch(idleDvf())
    dispatch(resetFormulaire())
  }

  const onNouvelleEtudeClick = () => {
    dispatch(idleDvf())
    dispatch(resetFormulaire())
  }

  const onEditClick = () => {
    toggleDisplayMode('normal')
    dispatch(idleDvf())
    if (map && principalBounds) {
      map.fitBounds(principalBounds)
    }
  }

  const onCardDvfClick = (uid: string) => {
    dispatch(selectParcelle(uid))
  }

  const onMarkerClick = (uid: string) => {
    dispatch(selectParcelle(uid))
  }

  const exportToCsv = async () => {
    dispatch(loadingExportFile())
    try {
      const response = await fetchWithCheckAuthorization(
        '/api/search/dvf/export',
        {
          method: 'POST',
          body: JSON.stringify(getDataForm()),
        }
      )
      if (response.ok && response.status === 200) {
        const blob = await response.blob()
        dispatch(succeededExportFile(blob))
        return
      }
      switch (response.status) {
        case 401:
          throw Error(response.statusText)
        case 500:
          throw Error('Erreur interne')
        default:
          throw Error('Une erreur est survenue')
      }
    } catch (err: unknown) {
      console.error(err)
      dispatch(failedExportFile((err as Error).toString()))
    }
  }

  useEffect(() => {
    const downloadCsv = (fileBlob: Blob) => {
      const href = window.URL.createObjectURL(fileBlob)
      const link = document.createElement('a')
      link.href = href
      link.setAttribute(
        'download',
        `dvf_${new Date().toJSON().slice(0, 10)}.csv`
      )
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }

    if (statusExportFile === Status.Succeeded && exportFileBlob !== null) {
      downloadCsv(exportFileBlob)
      dispatch(idleExportFile())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Triggers an error
  }, [statusExportFile, exportFileBlob])

  useEffect(() => {
    if (statusDvf === Status.ShowParcelleResults) {
      toggleDisplayMode('synthese')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Will trigger at unwanted times
  }, [statusDvf])

  return {
    locations,
    typeBien,
    nombrePieces,
    surfaceHabitableMin,
    surfaceHabitableMax,
    surfaceTerrainMin,
    surfaceTerrainMax,
    montantMin,
    montantMax,
    startDate,
    endDate,
    setLocations,
    setTypeDeBien,
    setNombrePieces,
    setSurfaceHabitableMin,
    setSurfaceHabitableMax,
    setSurfaceTerrainMin,
    setSurfaceTerrainMax,
    setMontantMin,
    setMontantMax,
    setStartDate,
    setEndDate,
    onSubmit: submitForm,
    onEffacerCriteresClick,
    onNouvelleEtudeClick,
    onEditClick,
    onCardDvfClick,
    onMarkerClick,
    exportToCsv,
    parcelles,
    mutations,
    statusDvf,
    errorDvf,
    displayMode,
    nextParcelle,
    previousParcelle,
    isNextAvailable,
    isPreviousAvailable,
    placeholderForm,
  }
}

export default useEtudierMarche
